import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import translations from '../data/translations.json'; // Import translations.json

export const LANGUAGES = [
    { code: 'en', label: '🇬🇧 English' },
    { code: 'de', label: '🇩🇪 German | Deutsch' },
    { code: 'fr', label: '🇫🇷 French | Français' },
    { code: 'pl', label: '🇵🇱 Polish | Polski' },
    { code: 'it', label: '🇮🇹 Italian | Italiano' },
    { code: 'ro', label: '🇷🇴 Romanian | Română' },
];

const CODES = LANGUAGES.map((l) => l.code);
const STORAGE_KEY = 'mfc.lang';

// A ?lang= in the address wins, so a German-speaking parent can send someone a
// link that opens in German. Otherwise fall back to what this visitor chose
// last time, then to the browser's own language.
function initialLanguage() {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('lang');
        if (fromUrl && CODES.includes(fromUrl)) return fromUrl;

        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored && CODES.includes(stored)) return stored;

        const browser = (window.navigator.language || '').slice(0, 2).toLowerCase();
        if (CODES.includes(browser)) return browser;
    } catch (e) {
        // Private browsing can throw on localStorage; English is a fine default.
    }
    return 'en';
}

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    const [lang, setLanguage] = useState(initialLanguage);

    useEffect(() => {
        // Screen readers use this to pick the right pronunciation, so it has to
        // follow the language actually on screen.
        document.documentElement.lang = lang;
        try {
            window.localStorage.setItem(STORAGE_KEY, lang);
        } catch (e) {
            // Nothing to do if storage is unavailable.
        }
    }, [lang]);

    const value = useMemo(() => {
        return {
            lang,
            setLanguage,
            languages: LANGUAGES,
            translate: (key) => {
                if (translations[lang] && translations[lang][key]) {
                    return translations[lang][key];
                }
                if (translations.en[key]) {
                    return translations.en[key];
                }
                return key; // Return key if translation is missing
            },
        };
    }, [lang]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    return useContext(LanguageContext);
};
